import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList as List } from 'react-window';
import cn from 'classnames';

import {
	Confirm,
	ExportActionsTypes,
	FileTypeSelectionCommonForm,
	FullChartCard,
	Modal,
	SpinnerOverlay,
} from '@components/common';
import PercentsSpinner from '@components/common/PercentsSpinner';

import { FULL_PERCENT } from '@hooks/constants';

import { kpi } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setActiveCharts } from '@slices/kpi';

import { checkIsEmptyChart } from '@utils/kpi-utils/chart';
import { sliceIntoChunks } from '@utils/kpi-utils/common';

import { IExportKpiDTO } from '@interfaces/kpi/dto/kpiDTO';
import { IChart } from '@interfaces/kpi/kpiCommon';
import { IExportFileDTO } from '@interfaces/platform/dto/platformDashboardDTO';
import {
	IChangedSizeChartsTypes,
	IChartsTypes,
	IRowChartsProps,
} from './Charts.types';

import { EVendorsValues } from '@enums/common';
import { EAggregationTypes } from '@enums/kpi-api';

import { exportRadioButtonsData } from '@constants/common';
import { MODAL_WIDTH_455 } from '@constants/modals';
import {
	ADDITIONAL_ELEMENTS_STATIC_HEIGHT,
	CHART_HEIGHT_ON_DASHBOARD,
	CHART_HEIGHT_ON_MAP,
	CLOSE_CHART_HEIGHT,
	ONE_COLUMN,
	PADDING_FILTER,
	SIE_ID_HEIGHT,
	TWO_COLUMN,
} from '@components/Kpi/Charts/constants';

import ChartCard from './ChartCard';
import CommonControlPanel from './CommonControlPanel';

import './Charts.scss';

const Charts = ({
	charts,
	isLoadingCharts,
	drillDownCharts,
	getDrillDownCharts,
	isWithInterval,
	isLoadingDrillDownCharts,
	compareSettings,
	addChartToCompare,
	compareCharts,
	addChartToCorrelate,
	correlateCharts,
	correlateSettings,
	exportCharts,
	isMap = false,
	errorTextKpi,
	panTo,
	progress,
	vendor,
}: IChartsTypes) => {
	const dispatch = useAppDispatch();
	const listRef = useRef();
	const isOranVendor = vendor === EVendorsValues.oran;
	const isLimenetVendor = vendor === EVendorsValues.limenet;

	const { chartsReceivedAt } = useAppSelector(kpi);

	const [allCharts, setAllCharts] = useState<Array<IChart>>(
		charts.map(chat => ({ ...chat, isOpen: true, isThreshold: false })),
	);
	const [selectedIds, setSelectedIds] = useState<Array<string>>([]);
	const [visibleThresholdIds, setVisibleThresholdIds] = useState<Array<string>>([]);
	const [changedSizeCharts, setChangedSizeCharts] = useState<IChangedSizeChartsTypes>();
	const [fullChart, setFullChart] = useState<IChart>();
	const [deletedChartIds, setDeletedChartIds] = useState<string[]>([]);
	const [deleteChartId, setDeleteChartId] = useState<string>();
	const [exportChart, setExportChart] = useState<IChart>();

	const [isOpenDeleteConfirmCard, setIsOpenDeleteConfirmCard] = useState(false);
	const [isOpenFileTypeSelectionModal, setIsOpenFileTypeSelectionModal] = useState(false);
	const [isHideEmptyCharts, setIsHideEmptyCharts] = useState(false);

	const aggregationType = useMemo(() => charts[0]?.aggregationType.name, [charts]);

	const matrixCharts: Array<IChart>[] = useMemo(() => (
		sliceIntoChunks(allCharts, isMap ? ONE_COLUMN : TWO_COLUMN)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	), [allCharts]);

	const toggleHideEmptyCharts = (isHide: boolean) => {
		setIsHideEmptyCharts(isHide);
		setAllCharts(prevState => (
			isHide
				? prevState.filter(chart => !checkIsEmptyChart(chart))
				: charts.map(chat => ({ ...chat, isOpen: true, isThreshold: false }))
					.filter(currentChart => !deletedChartIds.includes(currentChart.chartId))
		));

		repaintAllList();
	};

	const nonEmptyCharts = useMemo(() => allCharts.filter(chart => !checkIsEmptyChart(chart)), [allCharts]);

	useEffect(() => {
		dispatch(setActiveCharts(nonEmptyCharts));
	}, [dispatch, nonEmptyCharts]);

	useEffect(() => {
		repaintAllList();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allCharts]);

	const closeFullCard = () => setFullChart(undefined);

	const openDeleteConfirm = ({ chartId }: { chartId?: string }) => {
		setIsOpenDeleteConfirmCard(true);
		setDeleteChartId(chartId);
	};

	const closeDeleteConfirm = () => {
		setIsOpenDeleteConfirmCard(false);
		setDeleteChartId(undefined);
	};

	const openFileTypeSelectionModal = ({ chart }: { chart: IChart }) => {
		setExportChart(chart);
		setIsOpenFileTypeSelectionModal(true);
	};

	const closeTypeSelectionForm = () => {
		setExportChart(undefined);
		setIsOpenFileTypeSelectionModal(false);
	};

	const exportHandler = (data: IExportFileDTO) => {
		if (!exportChart || !chartsReceivedAt) return;

		const preparedTimeAggregationType = isOranVendor ? undefined : exportChart.yaxisAggregationType.name;
		const granularity = isOranVendor ? exportChart.granularity?.name : undefined;

		const exportData: Array<IExportKpiDTO> = [{
			fileType: data.fileType,
			aggregationType: exportChart.aggregationType.name,
			from: exportChart.from,
			to: exportChart.to,
			lastSeenTime: chartsReceivedAt,
			kpiIds: [exportChart.kpiId],
			sites: exportChart.sites,
			timeAggregationType: preparedTimeAggregationType,
			vendor: exportChart.vendor.name,
			granularity,
		}];

		exportCharts(exportData);
	};

	const repaintAllList = () => {
		if (listRef?.current) {
			for (let i = 0; i < matrixCharts.length; i++) {
				(listRef.current as any).resetAfterIndex(i, true);
			}
		}
	};

	const removeChart = () => {
		setAllCharts(prevState => prevState.filter(currentChart => currentChart.chartId !== deleteChartId));
		setSelectedIds(prevState => prevState.filter(id => id !== deleteChartId));
		if (deleteChartId) setDeletedChartIds(prevState => ([...prevState, deleteChartId]));
		closeDeleteConfirm();
	};

	useEffect(() => {
		setAllCharts(charts
			.map(chat => ({ ...chat, isOpen: true, isThreshold: false }))
			.filter(chart => isHideEmptyCharts ? !checkIsEmptyChart(chart) : true),
		);
		setSelectedIds([]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [charts.length]);

	useEffect(() => {
		if (!changedSizeCharts) return;

		const listOfCollapsableIndex = matrixCharts.reduce<Array<number>>((acc, charts, index) => {
			if (charts.some(chart => changedSizeCharts.ids.includes(chart.chartId))) {
				return [...acc, index];
			}

			return acc;
		}, []);

		setChangedSizeCharts(undefined);

		if (listOfCollapsableIndex.length && listRef?.current) {
			listOfCollapsableIndex.forEach(index => {
				(listRef.current as any).resetAfterIndex(index, true);
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [changedSizeCharts]);

	const Row = ({ data, style, index }: IRowChartsProps) => {
		const rowCharts = data[index];
		const leftChart: IChart = rowCharts[0];
		const rightChart: IChart = rowCharts[1];

		return (
			<div style={style}>
				<div className='f5gChartsVirtualizedItem'>
					{leftChart && (
						<ChartCard
							key={leftChart.chartId}
							addChartToCompare={addChartToCompare}
							addChartToCorrelate={addChartToCorrelate}
							chart={leftChart}
							compareCharts={compareCharts}
							compareSettings={compareSettings}
							correlateCharts={correlateCharts}
							correlateSettings={correlateSettings}
							height={CHART_HEIGHT_ON_DASHBOARD}
							onDeleteChart={openDeleteConfirm}
							openFileTypeSelectionModal={openFileTypeSelectionModal}
							panTo={panTo}
							selectedIds={selectedIds}
							setAllCharts={setAllCharts}
							setChangedSizeCharts={setChangedSizeCharts}
							setFullChart={setFullChart}
							setSelectedIds={setSelectedIds}
							setVisibleThresholdIds={setVisibleThresholdIds}
							visibleThresholdIds={visibleThresholdIds}
						/>
					)}
					{rightChart
						? <ChartCard
							key={rightChart.chartId}
							addChartToCompare={addChartToCompare}
							addChartToCorrelate={addChartToCorrelate}
							chart={rightChart}
							compareCharts={compareCharts}
							compareSettings={compareSettings}
							correlateCharts={correlateCharts}
							correlateSettings={correlateSettings}
							height={CHART_HEIGHT_ON_DASHBOARD}
							onDeleteChart={openDeleteConfirm}
							openFileTypeSelectionModal={openFileTypeSelectionModal}
							panTo={panTo}
							selectedIds={selectedIds}
							setAllCharts={setAllCharts}
							setChangedSizeCharts={setChangedSizeCharts}
							setFullChart={setFullChart}
							setSelectedIds={setSelectedIds}
							setVisibleThresholdIds={setVisibleThresholdIds}
							visibleThresholdIds={visibleThresholdIds}
						/>
						: <div style={{ flex: '1' }} />
					}
				</div>
			</div>
		);
	};

	const RowOnMap = ({ data, style, index }: IRowChartsProps) => {
		const chart: IChart = data[index][0];

		return (
			<div style={style}>
				<ChartCard
					key={chart.chartId}
					addChartToCompare={addChartToCompare}
					addChartToCorrelate={addChartToCorrelate}
					chart={chart}
					compareCharts={compareCharts}
					compareSettings={compareSettings}
					correlateCharts={correlateCharts}
					correlateSettings={correlateSettings}
					height={CHART_HEIGHT_ON_MAP}
					isMap
					onDeleteChart={openDeleteConfirm}
					openFileTypeSelectionModal={openFileTypeSelectionModal}
					panTo={panTo}
					selectedIds={selectedIds}
					setAllCharts={setAllCharts}
					setChangedSizeCharts={setChangedSizeCharts}
					setFullChart={setFullChart}
					setSelectedIds={setSelectedIds}
					setVisibleThresholdIds={setVisibleThresholdIds}
					visibleThresholdIds={visibleThresholdIds}
				/>
			</div>
		);
	};

	const getItemSize = (index: number, _height: number) => {
		const isNotAggregatedType = aggregationType !== EAggregationTypes.aggregated;
		const rowCharts = matrixCharts[index];
		const leftChart: IChart = rowCharts[0];
		const rightChart: IChart = rowCharts[1];

		const areAllClosed = !leftChart?.isOpen && !rightChart?.isOpen;
		const isChartClosed = !leftChart?.isOpen;

		if (isMap) {
			if (isChartClosed) return isNotAggregatedType ? CLOSE_CHART_HEIGHT + SIE_ID_HEIGHT : CLOSE_CHART_HEIGHT;

			return isNotAggregatedType
				? CHART_HEIGHT_ON_MAP + ADDITIONAL_ELEMENTS_STATIC_HEIGHT + SIE_ID_HEIGHT
				: CHART_HEIGHT_ON_MAP + ADDITIONAL_ELEMENTS_STATIC_HEIGHT;
		}

		if (areAllClosed) return isNotAggregatedType ? CLOSE_CHART_HEIGHT + SIE_ID_HEIGHT : CLOSE_CHART_HEIGHT;

		return isNotAggregatedType
			? CHART_HEIGHT_ON_DASHBOARD + ADDITIONAL_ELEMENTS_STATIC_HEIGHT + SIE_ID_HEIGHT
			: CHART_HEIGHT_ON_DASHBOARD + ADDITIONAL_ELEMENTS_STATIC_HEIGHT;
	};

	const isShowPercentsSpinner = (isOranVendor || isLimenetVendor)
		&& progress && progress?.percentsProgress !== FULL_PERCENT;

	return (
		<div className={cn('f5gChartsContainer', { 'f5gChartContainerOnMap': isMap })}>
			{isLoadingCharts && <SpinnerOverlay />}
			{isShowPercentsSpinner && (
				<PercentsSpinner
					isOverlay
					nameData='KPI/counter'
					percentsProgress={progress?.percentsProgress}
					savedBranches={progress?.savedKpis}
					totalBranchesToSave={progress?.totalKpisToSave}
				/>
			)}
			<Modal
				isOpen={isOpenFileTypeSelectionModal}
				onClose={closeTypeSelectionForm}
				title='File extension to export'
				width={MODAL_WIDTH_455}
			>
				<FileTypeSelectionCommonForm
					activeExportAction={ExportActionsTypes.all}
					closeModals={closeTypeSelectionForm}
					exportRadioButtonsData={exportRadioButtonsData}
					handlePreparingExport={exportHandler}
					isLoading={false}
					toggleModal={closeTypeSelectionForm}
				/>
			</Modal>
			<Modal
				isOpen={isOpenDeleteConfirmCard}
				onClose={closeDeleteConfirm}
			>
				<Confirm
					onClose={closeDeleteConfirm}
					onSubmit={removeChart}
					question='Are you sure you want to remove selected chart?'
				/>
			</Modal>
			<Modal
				isOpen={!!fullChart}
				onClose={closeFullCard}
				title={fullChart?.name || ''}
				width='80%'
			>
				{fullChart && (
					<FullChartCard
						addChartToCompare={addChartToCompare}
						addChartToCorrelate={addChartToCorrelate}
						chart={fullChart}
						compareCharts={compareCharts}
						compareSettings={compareSettings}
						correlateCharts={correlateCharts}
						correlateSettings={correlateSettings}
						drillDownCharts={drillDownCharts}
						exportCharts={exportCharts}
						getDrillDownCharts={getDrillDownCharts}
						isLoadingDrillDownCharts={isLoadingDrillDownCharts}
						isWithInterval={isWithInterval}
					/>
				)}
			</Modal>
			<CommonControlPanel
				allCharts={allCharts}
				exportCharts={exportCharts}
				isHideEmptyCharts={isHideEmptyCharts}
				isMap={isMap}
				panTo={panTo}
				selectedIds={selectedIds}
				setAllCharts={setAllCharts}
				setChangedSizeCharts={setChangedSizeCharts}
				setDeletedChartIds={setDeletedChartIds}
				setSelectedIds={setSelectedIds}
				setVisibleThresholdIds={setVisibleThresholdIds}
				toggleHideEmptyCharts={toggleHideEmptyCharts}
				visibleThresholdIds={visibleThresholdIds}
			/>
			{allCharts.length
				?
				<div className={cn('f5gChartsWrapper', { 'f5gChartsWrapperOnMap': isMap })}>
					<AutoSizer>
						{({ height = 0, width = 0 }) => (
							<List
								ref={listRef as any}
								height={height - PADDING_FILTER}
								itemCount={matrixCharts.length}
								itemData={matrixCharts}
								itemSize={(index: number) => getItemSize(index, height)}
								width={width}
							>
								{(data) => isMap ? RowOnMap(data) : Row(data)}
							</List>
						)}
					</AutoSizer>
				</div>
				: <div className='f5gChartsEmpty'>
					{charts.length
						? 'No KPI charts available'
						: errorTextKpi || 'Select parameters to generate charts'
					}
				</div>
			}
		</div>
	);
};

export default memo(Charts);
